import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import validator from 'validator'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import { canShowError } from 'subapps/onboarding/utils'
import { INDUSTRY_OPTIONS_V3 } from 'subapps/onboarding/sharedForm/constants'
import useFieldFocusChange from '../hooks/useFieldFocusChange'
import { styles } from './styles'
import { doSaveAndRedirect } from './actions'

const ecommerceValue = INDUSTRY_OPTIONS_V3[0].value
const softwareValue = INDUSTRY_OPTIONS_V3[1].value
const otherValue = INDUSTRY_OPTIONS_V3[2].value

const COMPANY_NAME = 'companyName'
const WEBSITE_URL = 'websiteUrl'
const INDUSTRY = 'industry'
const HEAR_ABOUT_US = 'hearAboutUs'

// Validation schema
const FORM_SCHEMA = yup.object().shape({
  [COMPANY_NAME]: yup.string().required('Company name is required'),
  [WEBSITE_URL]: yup
    .string()
    .nullable()
    .test(
      'URL',
      'Website URL is invalid',
      value => (value.trim() ? validator.isURL(value) : true)
    ),
  [INDUSTRY]: yup.string().required(),
  [HEAR_ABOUT_US]: yup.string().nullable(),
})

const defaultValues = {
  [COMPANY_NAME]: '',
  [WEBSITE_URL]: '',
  [INDUSTRY]: '',
  [HEAR_ABOUT_US]: '',
}

const CompanyPage = () => {
  const dispatch = useDispatch()

  const {
    register,
    handleSubmit,
    setFocus,
    watch,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(FORM_SCHEMA),
  })
  const submitDisabled = !isDirty || !isValid || isSubmitting
  const { first_name: firstName } = useSelector(selectCurrentUser)
  const greeting = firstName ? `Welcome, ${firstName}!` : 'Welcome!'
  const [focusedFields, setFocusedFields] = useState(null)
  const nameProps = useFieldFocusChange(
    register(COMPANY_NAME),
    setFocusedFields
  )
  const urlProps = useFieldFocusChange(register(WEBSITE_URL), setFocusedFields)

  const companyNameValue = watch(COMPANY_NAME)
  const websiteURLValue = watch(WEBSITE_URL)
  const companyNameError =
    errors[COMPANY_NAME] &&
    canShowError(focusedFields, COMPANY_NAME, companyNameValue)
      ? errors[COMPANY_NAME].message
      : undefined
  const websiteURLError =
    errors[WEBSITE_URL] &&
    canShowError(focusedFields, WEBSITE_URL, websiteURLValue)
      ? errors[WEBSITE_URL].message
      : undefined

  useEffect(
    () => {
      setFocus(COMPANY_NAME)
    },
    [setFocus]
  )

  const onSubmit = useCallback(
    async data => {
      // await, so isSubmitting can be true during submitting
      await dispatch(doSaveAndRedirect(data))
    },
    [dispatch]
  )

  return (
    <div css={styles.container}>
      <div css={styles.copy}>
        {greeting} Just a few questions to get you setup…
      </div>
      <div css={styles.formWrapper}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <h4 className="grui" css={styles.heading}>
            What’s your company name?
          </h4>
          <Field
            placeholder="e.g. NASA"
            css={styles.field}
            validateStatus={companyNameError ? 'error' : undefined}
            help={companyNameError}
            {...nameProps}
            autoComplete="off"
          />
          <h4 className="grui" css={styles.heading}>
            What’s your company website URL? <span>(optional)</span>
          </h4>
          <Field
            placeholder="e.g. www.mycompany.com"
            css={styles.field}
            validateStatus={websiteURLError ? 'error' : undefined}
            help={websiteURLError}
            {...urlProps}
            autoComplete="off"
          />

          <h4 className="grui" css={styles.heading}>
            How did you hear about Groove? <span>(optional)</span>
          </h4>
          <Field css={styles.field} {...register(HEAR_ABOUT_US)} />

          <h4 className="grui" css={styles.heading}>
            What industry is your business in?
          </h4>
          <div css={styles.radios}>
            <div css={styles.radioBtn}>
              <input
                id={ecommerceValue}
                type="radio"
                value={ecommerceValue}
                {...register(INDUSTRY)}
              />
              <label htmlFor={ecommerceValue}>{ecommerceValue}</label>
            </div>
            <div css={styles.radioBtn}>
              <input
                id={softwareValue}
                type="radio"
                value={softwareValue}
                {...register(INDUSTRY)}
              />
              <label htmlFor={softwareValue}>{softwareValue}</label>
            </div>
            <div css={styles.radioBtn}>
              <input
                id={otherValue}
                type="radio"
                value={otherValue}
                {...register(INDUSTRY)}
              />
              <label htmlFor={otherValue}>{otherValue}</label>
            </div>
          </div>

          <Button
            className="grui mt-13"
            htmlType="submit"
            type="info"
            size="xBig"
            css={styles.submitButton}
            disabled={submitDisabled}
          >
            Next
          </Button>
        </form>
      </div>
    </div>
  )
}

export default CompanyPage
